import { Helmet } from "react-helmet-async";
import { useLocation } from "react-router-dom";
import {
  BAKERY_ID,
  FB_URL,
  IG_URL,
  SERVICES_ENABLED,
  SITE_NAME,
  SITE_URL,
  isServicesPath,
} from "../common/constants";
import { getExtraPageSchema } from "../common/pageSchemas";
import { getFAQPageSchema } from "../pages/faqs/faqsData";

type SeoConfig = {
  title: string;
  description: string;
  keywords: string;
};

const DEFAULT_OG_IMAGE = `${SITE_URL}/apple-touch-icon.png`;

const SEO_BY_ROUTE: Record<string, SeoConfig> = {
  "/": {
    title: "Connie's Cakerie | Custom Desserts in Mountain View",
    description:
      "Connie's Cakerie is a boutique bakery in Mountain View serving the Bay Area with elegant custom cakes, cupcakes, decorated sugar cookies, cakesicles, wedding desserts, and fun decorating workshops.",
    keywords:
      "custom cakes mountain view, bay area bakery, silicon valley custom desserts, decorated sugar cookies, cupcakes, cakesicles, wedding desserts, activities to do in bay area, bay area workshops",
  },
  "/about": {
    title: "Connie's Cakerie | About the Bakery",
    description:
      "Connie's Cakerie is a Mountain View bakery specializing in luxury custom desserts made from scratch for birthdays, baby showers, weddings, and corporate events across the Bay Area.",
    keywords:
      "boutique cake shop, luxury desserts bay area, mountain view baker, custom wedding cakes, bay area corporate dessert orders",
  },
  "/menu": {
    title: "Connie's Cakerie | Custom Dessert Menu",
    description:
      "Browse custom dessert options including cakes, cupcakes, decorated sugar cookies, cakesicles, lemon bars, brownies, and fruit tarts for Bay Area events.",
    keywords:
      "custom dessert menu, cakes cupcakes cookies, cake pops, cakesicles, lemon bars brownies fruit tarts, mountain view bakery menu",
  },
  "/menu/cakes": {
    title: "Connie's Cakerie | Cake Flavors and Customizations",
    description:
      "Order elegant custom cakes for birthdays, baby showers, bridal showers, weddings, and corporate events in Mountain View, Silicon Valley, and across the Bay Area.",
    keywords:
      "custom cakes mountain view, bay area wedding cakes, birthday cakes silicon valley, baby shower cakes, bridal shower cakes",
  },
  "/menu/cupcakes": {
    title: "Connie's Cakerie | Cupcake Flavors and Customizations",
    description:
      "Custom cupcakes for kids birthdays, showers, weddings, and corporate events in Mountain View and the Bay Area.",
    keywords:
      "custom cupcakes mountain view, bay area cupcakes, corporate dessert catering, birthday cupcakes",
  },
  "/menu/cookies": {
    title: "Connie's Cakerie | Decorated Sugar Cookies",
    description:
      "Hand-decorated royal icing sugar cookies for baby showers, birthdays, weddings, and corporate orders throughout the Bay Area. Made to order in Mountain View.",
    keywords:
      "decorated sugar cookies bay area, custom cookies mountain view, baby shower cookies, corporate cookies silicon valley",
  },
  "/menu/cakesicles": {
    title: "Connie's Cakerie | Hand-Dipped Cakesicles",
    description:
      "Order cakesicles for parties, showers, weddings, and corporate events in Mountain View and surrounding Bay Area cities.",
    keywords:
      "cakesicles, cake popsicles bay area, cake pops, party favors,custom dessert favors mountain view, party desserts silicon valley",
  },
  "/menu/tarts-bars-brownies": {
    title: "Connie's Cakerie | Fruit Tarts, Lemon Bars, and Brownies",
    description:
      "Order lemon bars, fruit tarts, and brownies for Bay Area dessert tables, office events, and parties.",
    keywords:
      "fruit tarts bay area, lemon bars mountain view, brownies silicon valley, dessert table bakery",
  },
  "/services": {
    title: "Connie's Cakerie | Services for Bay Area Events",
    description:
      "Custom desserts for private celebrations, weddings, corporate events, and holidays in the Bay Area, with equipment rental available.",
    keywords:
      "bay area bakery services, wedding cakes, corporate desserts, private event desserts, holiday cookie orders",
  },
  "/services/private-events": {
    title: "Connie's Cakerie | Custom Desserts for Private Events",
    description:
      "Custom cakes, cupcakes, and cookies for Bay Area birthdays, baby showers, engagement parties, and anniversaries. Pickup in Mountain View or local delivery.",
    keywords:
      "birthday cakes mountain view, baby shower desserts bay area, private event bakery, custom party desserts",
  },
  "/services/corporate-events": {
    title: "Connie's Cakerie | Custom Desserts for Corporate Events",
    description:
      "Custom desserts for Silicon Valley team gatherings, product launches, and client events, including branded cookies, cakes, and dessert spreads from my Mountain View bakery.",
    keywords:
      "corporate dessert catering silicon valley, branded cookies bay area, office celebration desserts, mountain view corporate bakery",
  },
  "/services/weddings": {
    title: "Connie's Cakerie | Custom Desserts for Weddings",
    description:
      "Custom wedding cakes and dessert tables for Bay Area venues, with design help, delivery, and on-site assembly.",
    keywords:
      "bay area wedding cakes, mountain view wedding bakery, wedding dessert table, custom wedding cake silicon valley",
  },
  "/services/holiday-orders": {
    title: "Connie's Cakerie | Holiday Dessert Orders",
    description:
      "Seasonal holiday cookies, cupcakes, and cakes for Bay Area gifting, hosting, and office celebrations.",
    keywords:
      "holiday cookies bay area, christmas cookies mountain view, seasonal dessert orders, holiday bakery silicon valley",
  },
  "/services/equipment-rental": {
    title: "Connie's Cakerie | Equipment Rental",
    description:
      "Rent cake and cupcake stands for your Bay Area event without having to worry about storage or transportation.",
    keywords:
      "cake stand rental bay area, cupcake stand rental, display stand rental",
  },
  "/classes": {
    title: "Connie's Cakerie | Bay Area Decorating Classes",
    description:
      "Interactive cake, cupcake, cookie, and fondant decorating classes in Mountain View. Popular for Cake4Kids volunteer bakers. Also great for corporate events, team building, friend groups, and date ideas across the Bay Area.",
    keywords:
      "cake decorating classes bay area, Cake4Kids volunteer decorating classes, Cake4Kids class reimbursement, cake decorating classes mountain view, cookie decorating class silicon valley, corporate team building activities, fun date ideas silicon valley, decorating workshops",
  },
  "/gallery": {
    title: "Connie's Cakerie | Dessert Gallery",
    description:
      "View custom dessert designs from Bay Area celebrations including kids birthdays, baby showers, bridal showers, weddings, corporate events, and more.",
    keywords:
      "custom cake gallery, wedding dessert gallery, baby shower desserts, luxury bakery portfolio, custom dessert designs",
  },
  "/order": {
    title: "Connie's Cakerie | Order Desserts for Your Event",
    description:
      "Submit an order inquiry for custom cakes, cupcakes, sugar cookies, cakesicles, and wedding desserts. Serving Mountain View, South Bay, Peninsula, and Silicon Valley.",
    keywords:
      "order custom cake mountain view, bay area custom bakery, wedding cake inquiry, corporate dessert order silicon valley",
  },
  "/faqs": {
    title: "Connie's Cakerie | FAQs",
    description:
      "Read frequently asked questions about ordering timelines, delivery, payment, dessert care, and custom design requests for Bay Area dessert orders.",
    keywords:
      "bakery faqs, custom cake order questions, wedding dessert faq, mountain view bakery policies",
  },
};

const NOT_FOUND_SEO: SeoConfig = {
  title: "Connie's Cakerie | Page Not Found",
  description:
    "This page isn't on the Connie's Cakerie menu. Head home to browse custom cakes, cookies, and decorating classes in Mountain View.",
  keywords: "",
};

function Seo() {
  const { pathname } = useLocation();
  const isUnpublishedServices = !SERVICES_ENABLED && isServicesPath(pathname);
  const isNotFound = isUnpublishedServices || !(pathname in SEO_BY_ROUTE);
  const routeSeo = isUnpublishedServices
    ? NOT_FOUND_SEO
    : (SEO_BY_ROUTE[pathname] ?? NOT_FOUND_SEO);
  const canonical = `${SITE_URL}${pathname}`;

  const extraSchema =
    pathname === "/faqs" ? getFAQPageSchema() : getExtraPageSchema(pathname);

  const bakerySchema = {
    "@type": "Bakery",
    "@id": BAKERY_ID,
    name: SITE_NAME,
    url: SITE_URL,
    image: DEFAULT_OG_IMAGE,
    email: "connie@conniescakerie.com",
    telephone: "+1-408-800-2092",
    address: {
      "@type": "PostalAddress",
      addressLocality: "Mountain View",
      addressRegion: "CA",
      addressCountry: "US",
    },
    areaServed: [
      "Mountain View",
      "Silicon Valley",
      "South Bay",
      "Peninsula",
      "San Francisco Bay Area",
    ],
    sameAs: [IG_URL, FB_URL],
    makesOffer: [
      "Custom cakes",
      "Cupcakes",
      "Decorated sugar cookies",
      "Cake pops",
      "Cakesicles",
      "Cake popsicles",
      "Lemon bars",
      "Brownies",
      "Fruit tarts",
      "Wedding desserts",
      "Corporate dessert orders",
      "Decorating classes",
    ],
  };

  const structuredData = extraSchema
    ? {
        "@context": "https://schema.org",
        "@graph": [bakerySchema, extraSchema],
      }
    : {
        "@context": "https://schema.org",
        ...bakerySchema,
      };

  return (
    <Helmet>
      <html lang="en" />
      <title>{routeSeo.title}</title>
      <meta name="description" content={routeSeo.description} />
      {!isNotFound && <meta name="keywords" content={routeSeo.keywords} />}
      {isNotFound && <meta name="robots" content="noindex, nofollow" />}
      <meta property="og:site_name" content={SITE_NAME} />
      <meta property="og:type" content="website" />
      <meta property="og:title" content={routeSeo.title} />
      <meta property="og:description" content={routeSeo.description} />
      {!isNotFound && <meta property="og:url" content={canonical} />}
      <meta property="og:image" content={DEFAULT_OG_IMAGE} />
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:title" content={routeSeo.title} />
      <meta name="twitter:description" content={routeSeo.description} />
      <meta name="twitter:image" content={DEFAULT_OG_IMAGE} />
      {!isNotFound && <link rel="canonical" href={canonical} />}
      {!isNotFound && (
        <script type="application/ld+json">
          {JSON.stringify(structuredData)}
        </script>
      )}
    </Helmet>
  );
}

export default Seo;
